import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import type { PartyListItemDto, PartySortBy, PartyType } from "@/types/parties";
import { PARTY_PERMISSIONS, partyBasePath } from "@/types/parties";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { NeedsAttentionBadge } from "@/components/portalAbsorption/NeedsAttentionBadge";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { usePartyList } from "@/hooks/parties/useParties";

// Party read-flip (WS-A) — the Borrower/Seller party registries. One page
// parameterized by PartyType (PersonListPage conventions) with the
// needs-attention sort + per-row badge from the portal-absorption spine.

const PAGE_SIZE = 25;

type SortChoice = "nameAsc" | "nameDesc" | "newest" | "needsAttention";

const SORT_CHOICES: Record<SortChoice, { label: string; sortBy: PartySortBy; sortDescending: boolean }> = {
  nameAsc: { label: "Name (A–Z)", sortBy: "displayName", sortDescending: false },
  nameDesc: { label: "Name (Z–A)", sortBy: "displayName", sortDescending: true },
  newest: { label: "Newest", sortBy: "createdAt", sortDescending: true },
  needsAttention: { label: "Needs attention", sortBy: "needsAttention", sortDescending: false },
};

export function PartiesListPage({ type }: { type: PartyType }) {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { date } = useDateFormatter();

  const perms = PARTY_PERMISSIONS[type];
  const base = partyBasePath(type);
  const noun = type === "Borrower" ? "borrower" : "seller";
  const title = type === "Borrower" ? "Borrowers" : "Sellers";

  const canCreate = hasPermission(perms.Create);

  const [search, setSearch] = useState("");
  const [sortChoice, setSortChoice] = useState<SortChoice>("nameAsc");
  const [page, setPage] = useState(1);

  useBreadcrumbs([{ label: title }]);

  useEffect(() => {
    setPage(1);
  }, [search, sortChoice]);

  const sort = SORT_CHOICES[sortChoice];
  const listQuery = usePartyList(type, {
    search: search || undefined,
    sortBy: sort.sortBy,
    sortDescending: sort.sortDescending,
    offset: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });

  const totalPages = useMemo(() => {
    const data = listQuery.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [listQuery.data]);

  const rows = listQuery.data?.data ?? [];

  const columns: Column<PartyListItemDto>[] = [
    {
      key: "name",
      header: "Name",
      render: (p) => (
        <div className="flex flex-wrap items-center gap-space-2">
          <Link to={`${base}/${p.id}`} className="font-semibold hover:text-gold-shadow">
            {p.displayName}
          </Link>
          {p.portalUserId && <NeedsAttentionBadge portalUserId={p.portalUserId} />}
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (p) =>
        displayEmail(p.email) ?? <span className="text-gray-mid">{NO_EMAIL_ON_FILE}</span>,
    },
    { key: "phone", header: "Phone", mobileHidden: true, render: (p) => p.phone || "—" },
    {
      key: "kind",
      header: "Kind",
      mobileHidden: true,
      render: (p) => (
        <Badge status={p.partyKind === "Business" ? "review" : "draft"}>{p.partyKind}</Badge>
      ),
    },
    {
      key: "location",
      header: "Location",
      mobileHidden: true,
      render: (p) => (p.city || p.state ? [p.city, p.state].filter(Boolean).join(", ") : "—"),
    },
    {
      key: "portal",
      header: "Portal",
      mobileHidden: true,
      render: (p) =>
        p.portalUserId ? (
          <Badge status="finalized">Linked</Badge>
        ) : (
          <span className="text-caption text-gray-mid">No account</span>
        ),
    },
    { key: "created", header: "Created", render: (p) => date(p.createdAt) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Parties"
        title={title}
        actions={
          canCreate && (
            <Button size="sm" onClick={() => navigate(`${base}/new`)}>
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New {noun}
            </Button>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search name, email, legal name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="grow"
          aria-label={`Search ${title.toLowerCase()}`}
        />
        <Select
          value={sortChoice}
          onChange={(e) => setSortChoice(e.target.value as SortChoice)}
          className="max-w-[12rem] shrink-0"
          aria-label="Sort"
        >
          {(Object.keys(SORT_CHOICES) as SortChoice[]).map((k) => (
            <option key={k} value={k}>
              {SORT_CHOICES[k].label}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={listQuery.isLoading}
        error={listQuery.isError ? (listQuery.error as Error).message : null}
        emptyMessage={
          canCreate
            ? `No ${title.toLowerCase()} match your filters. Click “New ${noun}” to add one.`
            : `No ${title.toLowerCase()} match your filters.`
        }
        rowKey={(p) => p.id}
        onRowClick={(p) => navigate(`${base}/${p.id}`)}
      />

      {listQuery.data && listQuery.data.totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={listQuery.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </>
  );
}

export function BorrowersPage() {
  return <PartiesListPage type="Borrower" />;
}

export function SellersPage() {
  return <PartiesListPage type="Seller" />;
}
